import { useEffect, useState } from "react";
import EmailGate from "../components/EmailGate";
import { hasStoredContentAccess } from "../lib/gatedContentTracking";

const FELLOWS_PROGRAM_ACCESS_KEY = "asha_ai_fellows_program_access";

const STYLES = `
.fellows-doc {
  --paper: #fcfcfd;
  --paper-2: #f4f5f8;
  --paper-3: #f8fafc;
  --ink: #1c2540;
  --ink-2: #404a66;
  --ink-3: #737d96;
  --line: #d8dde6;
  --line-2: #e7eaf0;
  --accent: #3a5bd9;
  --accent-deep: #2638b2;
  --accent-soft: #eef0fa;
  --accent-ink: #2c43c4;
  --accent-soft-2: #eaedf7;
  --accent-border: #bdc6e8;
  --green: #13795b;
  --green-soft: #e8f5ef;
  font-family: 'Inter', system-ui, sans-serif;
  color: var(--ink);
  font-size: 14px;
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

.fellows-doc *,
.fellows-doc *::before,
.fellows-doc *::after { box-sizing: border-box; }

.fellows-doc .page {
  max-width: 1040px;
  margin: 56px auto;
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 14px;
  box-shadow:
    0 1px 0 rgba(0,0,0,0.02),
    0 30px 60px -30px rgba(20,30,40,0.18),
    0 12px 24px -16px rgba(20,30,40,0.10);
  overflow: hidden;
}

.fellows-doc .topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 22px 56px;
  border-bottom: 1px solid var(--line);
  background: var(--paper);
}
.fellows-doc .brand {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Inter Tight', sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: var(--ink);
}
.fellows-doc .brand-mark {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  overflow: hidden;
  flex-shrink: 0;
}
.fellows-doc .brand-mark img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}
.fellows-doc .brand-meta {
  color: var(--ink-3);
  font-weight: 450;
}
.fellows-doc .hero {
  padding: 72px 56px 56px;
  border-bottom: 1px solid var(--line);
  position: relative;
  background:
    linear-gradient(180deg, rgba(238, 240, 250, 0.72), rgba(252, 252, 253, 0) 44%),
    var(--paper);
}
.fellows-doc .eyebrow {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 18px;
}
.fellows-doc .eyebrow b {
  color: var(--accent-ink);
  font-weight: 500;
}
.fellows-doc h1.title {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 56px;
  line-height: 1.04;
  margin: 0 0 14px;
  color: var(--ink);
  max-width: 18ch;
}
.fellows-doc .title-sub {
  font-family: 'Fraunces', serif;
  font-style: italic;
  font-weight: 300;
  font-size: 24px;
  color: var(--accent-ink);
  margin: 0 0 26px;
}
.fellows-doc .lede {
  font-size: 17px;
  line-height: 1.55;
  color: var(--ink-2);
  max-width: 66ch;
  margin: 0 0 42px;
  text-wrap: pretty;
}

.fellows-doc section.s {
  padding: 64px 56px;
  border-bottom: 1px solid var(--line);
}
.fellows-doc section.s:last-of-type { border-bottom: 0; }
.fellows-doc .soft-section {
  background: linear-gradient(180deg, var(--paper) 0%, #f7f7fb 100%);
}
.fellows-doc .s-head {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 32px;
  margin-bottom: 36px;
  align-items: baseline;
}
.fellows-doc .s-num {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-3);
  padding-top: 10px;
  border-top: 1px solid var(--ink);
  width: 52px;
}
.fellows-doc .s-title {
  font-family: 'Inter Tight', sans-serif;
  font-size: 34px;
  font-weight: 500;
  line-height: 1.1;
  color: var(--ink);
  margin: 0;
}
.fellows-doc .s-sub {
  font-size: 15px;
  color: var(--ink-2);
  margin-top: 10px;
  max-width: 64ch;
  text-wrap: pretty;
}
.fellows-doc .s-body {
  margin-left: 152px;
}

.fellows-doc .reason-grid,
.fellows-doc .contribution-grid,
.fellows-doc .trigger-grid {
  display: grid;
  gap: 14px;
}
.fellows-doc .reason-grid {
  grid-template-columns: repeat(3, 1fr);
}
.fellows-doc .contribution-grid {
  grid-template-columns: repeat(2, 1fr);
}
.fellows-doc .trigger-grid {
  grid-template-columns: repeat(3, 1fr);
}
.fellows-doc .info-card,
.fellows-doc .trigger-card,
.fellows-doc .contribution-card {
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--paper);
  padding: 22px 24px;
}
.fellows-doc .info-card.accent {
  background: linear-gradient(180deg, var(--accent-soft), rgba(234, 237, 247, 0.72));
  border-color: var(--accent-border);
}
.fellows-doc .info-card.green {
  background: var(--green-soft);
  border-color: #b8dfcf;
}
.fellows-doc .info-title,
.fellows-doc .trigger-title,
.fellows-doc .contribution-title {
  font-family: 'Inter Tight', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: var(--ink);
  margin-bottom: 8px;
}
.fellows-doc .info-copy,
.fellows-doc .trigger-copy,
.fellows-doc .contribution-copy {
  font-size: 13px;
  line-height: 1.55;
  color: var(--ink-2);
  margin: 0;
}
.fellows-doc .section-intro {
  color: var(--ink-2);
  font-size: 14px;
  line-height: 1.6;
  max-width: 80ch;
  margin: 0 0 22px;
}

.fellows-doc .timeline {
  display: grid;
  grid-template-columns: 1fr;
  border-top: 1px solid var(--line);
}
.fellows-doc .timeline-row {
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 28px;
  padding: 24px 0;
  border-bottom: 1px solid var(--line-2);
}
.fellows-doc .timeline-row:last-child { border-bottom: 0; }
.fellows-doc .timeline-time {
  font-family: 'Inter Tight', sans-serif;
  font-size: 20px;
  font-weight: 500;
  color: var(--accent-ink);
}
.fellows-doc .timeline-title {
  font-family: 'Inter Tight', sans-serif;
  font-size: 19px;
  font-weight: 500;
  color: var(--ink);
  margin-bottom: 6px;
}
.fellows-doc .timeline-copy {
  color: var(--ink-2);
  line-height: 1.6;
  margin: 0;
  max-width: 76ch;
}

.fellows-doc .mini-list {
  list-style: none;
  padding: 18px 0 0;
  margin: 20px 0 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 18px;
  border-top: 1px solid var(--line-2);
}
.fellows-doc .mini-list li {
  position: relative;
  padding-left: 18px;
  color: var(--ink-2);
  font-size: 13px;
}
.fellows-doc .mini-list li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 9px;
  width: 4px;
  height: 4px;
  border-radius: 99px;
  background: var(--accent);
}

.fellows-doc table.lines {
  width: 100%;
  border-collapse: collapse;
  margin-top: 4px;
}
.fellows-doc table.lines th,
.fellows-doc table.lines td {
  text-align: left;
  padding: 22px 0;
  vertical-align: top;
  border-bottom: 1px solid var(--line-2);
}
.fellows-doc table.lines thead th {
  padding: 0 0 14px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  font-weight: 500;
  border-bottom: 1px solid var(--ink);
}
.fellows-doc table.lines tbody tr:last-child td { border-bottom: 0; }
.fellows-doc .li-name {
  font-family: 'Inter Tight', sans-serif;
  font-size: 17px;
  font-weight: 500;
  color: var(--ink);
  margin-bottom: 6px;
}
.fellows-doc .li-desc,
.fellows-doc .li-note {
  font-size: 13px;
  color: var(--ink-2);
  line-height: 1.5;
  max-width: 54ch;
}
.fellows-doc .li-amount {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 21px;
  color: var(--ink);
  white-space: nowrap;
}
.fellows-doc .li-amount small {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: var(--ink-3);
  margin-top: 2px;
}
.fellows-doc .col-item { width: 34%; padding-right: 30px !important; }
.fellows-doc .col-details { width: 42%; padding-right: 30px !important; }
.fellows-doc .col-terms { width: 24%; text-align: right !important; }
.fellows-doc td.col-terms,
.fellows-doc th.col-terms { text-align: right !important; }

.fellows-doc .process-band {
  border: 1px solid var(--accent-border);
  border-radius: 12px;
  background: linear-gradient(180deg, var(--accent-soft), rgba(252, 252, 253, 0.88));
  padding: 24px 26px;
  margin-top: 24px;
}
.fellows-doc .process-title {
  font-family: 'Inter Tight', sans-serif;
  font-size: 20px;
  font-weight: 500;
  color: var(--ink);
  margin-bottom: 12px;
}
.fellows-doc .process-list {
  list-style: none;
  padding: 0;
  margin: 0;
  counter-reset: process;
}
.fellows-doc .process-list li {
  counter-increment: process;
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(189, 198, 232, 0.6);
  color: var(--ink-2);
}
.fellows-doc .process-list li:last-child { border-bottom: 0; }
.fellows-doc .process-list li::before {
  content: counter(process, decimal-leading-zero);
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: var(--accent-ink);
  padding-top: 2px;
}

.fellows-doc .expectation-copy h3 {
  font-family: 'Inter Tight', sans-serif;
  font-size: 20px;
  font-weight: 500;
  color: var(--ink);
  margin: 0 0 8px;
}
.fellows-doc .expectation-copy p {
  color: var(--ink-2);
  line-height: 1.6;
  margin: 0;
  max-width: 82ch;
}
.fellows-doc .expectation-copy b { color: var(--ink); font-weight: 500; }

.fellows-doc .faq-list {
  display: flex;
  flex-direction: column;
  gap: 22px;
  border-top: 1px solid var(--line);
  padding-top: 28px;
}
.fellows-doc .faq-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
  padding-bottom: 22px;
  border-bottom: 1px dashed var(--line-2);
}
.fellows-doc .faq-item:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}
.fellows-doc .faq-q {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 18px;
  color: var(--ink);
  margin: 0;
}
.fellows-doc .faq-a {
  font-size: 14px;
  line-height: 1.6;
  color: var(--ink-2);
  max-width: 82ch;
  margin: 0;
}
.fellows-doc .faq-a b { color: var(--ink); font-weight: 500; }

.fellows-doc .tail {
  padding: 22px 56px;
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: center;
  background: var(--paper);
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-3);
  border-top: 1px solid var(--line);
}

@media (max-width: 900px) {
  .fellows-doc .page { margin: 24px; }
  .fellows-doc .hero,
  .fellows-doc section.s,
  .fellows-doc .topbar,
  .fellows-doc .tail { padding-left: 28px; padding-right: 28px; }
  .fellows-doc h1.title { font-size: 44px; }
  .fellows-doc .s-head { grid-template-columns: 1fr; gap: 12px; }
  .fellows-doc .s-body { margin-left: 0; }
  .fellows-doc .reason-grid,
  .fellows-doc .contribution-grid,
  .fellows-doc .trigger-grid { grid-template-columns: 1fr; }
}

@media (max-width: 720px) {
  .fellows-doc .page { margin: 14px; border-radius: 14px; }
  .fellows-doc .hero,
  .fellows-doc section.s,
  .fellows-doc .topbar,
  .fellows-doc .tail {
    padding-left: 20px;
    padding-right: 20px;
  }
  .fellows-doc .topbar {
    padding-top: 14px;
    padding-bottom: 14px;
    align-items: flex-start;
    flex-direction: column;
  }
  .fellows-doc .hero { padding-top: 44px; padding-bottom: 36px; }
  .fellows-doc section.s { padding-top: 40px; padding-bottom: 40px; }
  .fellows-doc h1.title { font-size: 34px; max-width: none; }
  .fellows-doc .title-sub { font-size: 18px; margin-bottom: 20px; }
  .fellows-doc .lede { font-size: 15px; margin-bottom: 32px; }
  .fellows-doc .timeline-row { grid-template-columns: 1fr; gap: 8px; }
  .fellows-doc .mini-list { grid-template-columns: 1fr; }
  .fellows-doc table.lines,
  .fellows-doc table.lines tbody,
  .fellows-doc table.lines tr,
  .fellows-doc table.lines td {
    display: block;
    width: auto !important;
  }
  .fellows-doc table.lines thead { display: none; }
  .fellows-doc table.lines tr {
    padding: 22px 0;
    border-bottom: 1px solid var(--line-2);
  }
  .fellows-doc table.lines tbody tr:last-child { border-bottom: 0; }
  .fellows-doc table.lines td {
    padding: 0 !important;
    border: 0 !important;
  }
  .fellows-doc table.lines td + td { margin-top: 14px; }
  .fellows-doc table.lines td.col-terms { text-align: left !important; }
  .fellows-doc .tail {
    flex-direction: column;
    align-items: flex-start;
    padding-top: 16px;
    padding-bottom: 16px;
  }
}

@media print {
  @page {
    size: letter;
    margin: 0.3in;
  }
  html, body {
    background: #fff !important;
  }
  body.fellows-body { background: #fff !important; }
  .fellows-doc { font-size: 11px !important; }
  .fellows-doc .page {
    max-width: 100% !important;
    margin: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
  .fellows-doc .topbar { padding: 14px 24px; }
  .fellows-doc .hero { padding: 32px 24px 24px; }
  .fellows-doc h1.title { font-size: 32px; max-width: none; }
  .fellows-doc .title-sub { font-size: 16px; margin-bottom: 14px; }
  .fellows-doc .lede { margin-bottom: 20px; font-size: 12px; }
  .fellows-doc section.s { padding: 24px; }
  .fellows-doc .s-head {
    grid-template-columns: 1fr;
    gap: 8px;
    margin-bottom: 16px;
  }
  .fellows-doc .s-title { font-size: 22px; }
  .fellows-doc .s-body { margin-left: 0; }
  .fellows-doc .tail { padding: 12px 24px; }
  .fellows-doc .reason-grid,
  .fellows-doc .contribution-grid,
  .fellows-doc .trigger-grid { grid-template-columns: 1fr; }
  .fellows-doc .timeline-row,
  .fellows-doc .info-card,
  .fellows-doc .trigger-card,
  .fellows-doc .contribution-card,
  .fellows-doc .expectation-copy,
  .fellows-doc table.lines tr,
  .fellows-doc .faq-item {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
`;

const AIFellowsProgramPage = () => {
  const [isLocked, setIsLocked] = useState(true);

  useEffect(() => {
    if (hasStoredContentAccess(FELLOWS_PROGRAM_ACCESS_KEY)) setIsLocked(false);
  }, []);

  const handleAccessGranted = (email) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(FELLOWS_PROGRAM_ACCESS_KEY, "true");
    }
    // eslint-disable-next-line no-console
    console.log("AI Fellows program access granted for:", email);
    setIsLocked(false);
  };

  useEffect(() => {
    const link1 = document.createElement("link");
    link1.rel = "preconnect";
    link1.href = "https://fonts.googleapis.com";

    const link2 = document.createElement("link");
    link2.rel = "preconnect";
    link2.href = "https://fonts.gstatic.com";
    link2.crossOrigin = "";

    const link3 = document.createElement("link");
    link3.rel = "stylesheet";
    link3.href =
      "https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700&family=Inter:wght@400;450;500;600&family=JetBrains+Mono:wght@400;500&family=Fraunces:opsz,wght@9..144,300;9..144,400&display=swap";

    document.head.appendChild(link1);
    document.head.appendChild(link2);
    document.head.appendChild(link3);

    const previousBg = document.body.style.background;
    const previousMargin = document.body.style.margin;
    document.body.style.background = "#f1efe7";
    document.body.style.margin = "0";
    document.body.classList.add("fellows-body");
    document.title = "Asha — AI Innovation Fellows Program";

    return () => {
      document.head.removeChild(link1);
      document.head.removeChild(link2);
      document.head.removeChild(link3);
      document.body.style.background = previousBg;
      document.body.style.margin = previousMargin;
      document.body.classList.remove("fellows-body");
    };
  }, []);

  return (
    <div className="fellows-doc">
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      {isLocked && (
        <EmailGate
          onAccessGranted={handleAccessGranted}
          title="Please enter your email to view this page"
          description="Access to the Asha Health AI Innovation Fellows overview is restricted."
          submitLabel="View page"
          agreementNoun="this page"
          confidentialityText="The information on this page is strictly confidential. Sharing any information without prior written consent from Asha Health, Inc. would be a violation of the NDA you accepted in the previous step."
        />
      )}

      <div className="page">
        <div className="topbar">
          <div className="brand">
            <div className="brand-mark">
              <img src="/assets/alogo.png" alt="Asha" />
            </div>
            <span>Asha Health</span>
            <span className="brand-meta">· asha.dental</span>
          </div>
        </div>

        <div className="hero">
          <div className="eyebrow">
            <b>AI Innovation Fellows</b> &nbsp;·&nbsp; How the program works
          </div>
          <h1 className="title">Build the future of AI in dentistry with us</h1>
          <div className="title-sub">
            For dentists who want to help shape Asha, teach what works, and bring the dental community forward
          </div>
          <p className="lede">
            We created the Fellows program because dentists were asking how to
            become more involved as key opinion leaders, and because the
            excitement around AI in dentistry was turning into a practical
            question: how do we build AI that offices genuinely love using?
            Asha is venture-backed, which means our incentives are aligned
            around durable customer love. We only win if our customers win with
            the platform.
          </p>

        </div>

        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 01</div>
            <div>
              <h2 className="s-title">Why this exists</h2>
              <p className="s-sub">
                We started the program because dentists wanted a real role in
                shaping AI for dentistry, not just a logo on a marketing page.
              </p>
            </div>
          </div>

          <div className="s-body">
            <div className="reason-grid">
              <div className="info-card">
                <div className="info-title">Dentists were asking how to get involved</div>
                <p className="info-copy">
                  We were receiving frequent requests from dentists who wanted
                  to be KOLs, share what they were learning, and help define how
                  AI should work inside dental offices.
                </p>
              </div>
              <div className="info-card accent">
                <div className="info-title">Asha has to build something customers truly love</div>
                <p className="info-copy">
                  Private-equity-backed companies often center decisions around
                  margin. Venture-backed companies only become great if
                  customers love the product enough to create durable growth.
                </p>
              </div>
              <div className="info-card green">
                <div className="info-title">The platform should be built with the dental community</div>
                <p className="info-copy">
                  Dental AI cannot be built well from a conference room alone.
                  The program gives practicing teams a direct role in shaping
                  the Asha operating system and agent roadmap.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="s soft-section">
          <div className="s-head">
            <div className="s-num">§ 02</div>
            <div>
              <h2 className="s-title">The first twelve weeks</h2>
              <p className="s-sub">
                Before participating as a Fellow, the most valuable thing you
                can do is become fluent in the platform as a customer.
              </p>
            </div>
          </div>

          <div className="s-body">
            <div className="timeline">
              <div className="timeline-row">
                <div>
                  <div className="timeline-time">Weeks 0-4</div>
                </div>
                <div>
                  <div className="timeline-title">Onboarding</div>
                  <p className="timeline-copy">
                    Your account manager helps configure your settings,
                    integrate Asha with your office, and calibrate the AI to how
                    your team actually works. The first month is about getting
                    the foundations in place so the platform can run reliably
                    day to day.
                  </p>
                </div>
              </div>

              <div className="timeline-row">
                <div>
                  <div className="timeline-time">Weeks 5-12</div>
                </div>
                <div>
                  <div className="timeline-title">Become fluent and operationally confident</div>
                  <p className="timeline-copy">
                    During this phase, you become really familiar with the AI,
                    find it effective for your office, and develop the
                    operational experience to even advise other offices. This is
                    when you learn where AI is strong, where it needs
                    guardrails, and how to get reliable outcomes from the
                    platform.
                  </p>
                </div>
              </div>

              <div className="timeline-row">
                <div>
                  <div className="timeline-time">Weeks 12 onwards</div>
                </div>
                <div>
                  <div className="timeline-title">Join the Fellows network led by Dr. Smith</div>
                  <p className="timeline-copy">
                    From week twelve onwards, you can take advantage of Fellows
                    program benefits. You are added to the Fellows network led
                    by Dr. Smith, Chief Medical Officer of the MSU Health
                    System. You also receive access to the Fellows Slack
                    channel. Slack is the internal communication software Asha
                    uses, and this channel gives you a direct line to Asha's
                    product team and other Innovation Fellows.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 03</div>
            <div>
              <h2 className="s-title">How Fellows contribute</h2>
              <p className="s-sub">
                You do not have to contribute in one prescribed format. The goal
                is to share what you learn, help us improve the product, and
                help other offices adopt AI responsibly.
              </p>
            </div>
          </div>

          <div className="s-body">
            <div className="contribution-grid">
              <div className="contribution-card">
                <div className="contribution-title">Teach other offices what actually worked</div>
                <p className="contribution-copy">
                  Create practical content showing how you used a specific AI
                  workflow in your office, what changed, and what outcomes it
                  produced. We can spotlight your content if you want that
                  visibility.
                </p>
              </div>
              <div className="contribution-card">
                <div className="contribution-title">Give direct product feedback</div>
                <p className="contribution-copy">
                  Use the Fellows Slack channel to discuss ideas, surface
                  workflow gaps, and give direct feedback to the product team on
                  what should be built next.
                </p>
              </div>
              <div className="contribution-card">
                <div className="contribution-title">Test new features before broad launch</div>
                <p className="contribution-copy">
                  When the product team needs real-world feedback on unreleased
                  features, Fellows may be invited to test them before they
                  launch broadly.
                </p>
              </div>
              <div className="contribution-card">
                <div className="contribution-title">Collaborate on articles, studies, and advisory content</div>
                <p className="contribution-copy">
                  Fellows can collaborate with Asha on articles, studies, and
                  advisory content that helps other practices understand how to
                  manage AI adoption responsibly.
                </p>
              </div>
              <div className="contribution-card">
                <div className="contribution-title">Offer consulting to offices adopting AI</div>
                <p className="contribution-copy">
                  Once you have real operating experience, you can offer paid
                  consulting services to offices that are bringing AI into their
                  practice — covering staff change management, workflow
                  rollout, agent calibration, or office growth and acquisitions.
                </p>
              </div>
              <div className="contribution-card">
                <div className="contribution-title">Build AI agents for the Asha marketplace</div>
                <p className="contribution-copy">
                  Use the Clinician Creator Suite to turn your clinical,
                  administrative, or operational reasoning into AI agents you
                  can list on the upcoming Asha agent marketplace, free or paid.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="s soft-section">
          <div className="s-head">
            <div className="s-num">§ 04</div>
            <div>
              <h2 className="s-title">Program opportunities</h2>
              <p className="s-sub">
                Fellows receive preferred platform pricing and can participate
                in optional commercial opportunities once they understand the
                platform well enough to represent it accurately.
              </p>
            </div>
          </div>

          <div className="s-body">
            <table className="lines">
              <thead>
                <tr>
                  <th className="col-item">Opportunity</th>
                  <th className="col-details">How it works</th>
                  <th className="col-terms">Terms</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="col-item">
                    <div className="li-name">Fellows-only pricing</div>
                    <div className="li-desc">
                      Fellows receive discounted pricing on the platform as an
                      exception to Asha's standard no-discount policy.
                    </div>
                  </td>
                  <td className="col-details">
                    <div className="li-note">
                      Preferred pricing is tied to active participation in the
                      program and continued adherence to program rules.
                    </div>
                  </td>
                  <td className="col-terms">
                    <div className="li-amount">
                      Discounted<small>platform pricing</small>
                    </div>
                  </td>
                </tr>

                <tr>
                  <td className="col-item">
                    <div className="li-name">Dentist partner revenue share</div>
                    <div className="li-desc">
                      If you refer offices that sign up as Asha customers, you
                      can receive a revenue share after you have been approved
                      as knowledgeable enough to share how the platform works.
                    </div>
                  </td>
                  <td className="col-details">
                    <div className="li-note">
                      Eligibility begins after the first twelve weeks and
                      only after Asha confirms you understand the platform well
                      enough to discuss it accurately. Sharing must be
                      one-on-one; Fellows may not mass advertise on Asha's
                      behalf.
                    </div>
                  </td>
                  <td className="col-terms">
                    <div className="li-amount">
                      12%<small>of net revenue, first year; $100k cap per referred entity</small>
                    </div>
                  </td>
                </tr>

                <tr>
                  <td className="col-item">
                    <div className="li-name">Consulting services</div>
                    <div className="li-desc">
                      Once you are experienced with AI adoption in your own
                      office, you can list paid consulting services for other
                      practices.
                    </div>
                  </td>
                  <td className="col-details">
                    <div className="li-note">
                      Topics may include staff change management, AI workflow
                      adoption, getting more value from specific agents, office
                      growth, acquisitions, and operating with or around PE.
                    </div>
                  </td>
                  <td className="col-terms">
                    <div className="li-amount">
                      Optional<small>paid advisory listings</small>
                    </div>
                  </td>
                </tr>

                <tr>
                  <td className="col-item">
                    <div className="li-name">Thought leader spotlight</div>
                    <div className="li-desc">
                      After your first contributions, Asha can spotlight your
                      practical AI learnings, articles, videos, or studies.
                    </div>
                  </td>
                  <td className="col-details">
                    <div className="li-note">
                      Spotlighting is optional and based on what you choose to
                      create with the team.
                    </div>
                  </td>
                  <td className="col-terms">
                    <div className="li-amount">
                      Optional<small>visibility and distribution</small>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 05</div>
            <div>
              <h2 className="s-title">Clinician Creator Suite</h2>
              <p className="s-sub">
                Fellows can use the beta Clinician Creator Suite to turn clinical,
                administrative, or operational reasoning into AI agents that can
                later be listed on the upcoming Asha agent marketplace.
              </p>
            </div>
          </div>

          <div className="s-body">
            <p className="section-intro">
              For now, the Clinician Creator Suite supports three ways to start an AI
              agent. Each one can kick off a process where you describe your
              clinical, administrative, or operational reasoning in plain
              English and the AI produces an output from it.
            </p>
            <div className="trigger-grid">
              <div className="trigger-card">
                <div className="trigger-title">Run after something happens in the office</div>
                <p className="trigger-copy">
                  An agent can run when something happens in the office, such
                  as a patient getting scheduled.
                </p>
              </div>
              <div className="trigger-card">
                <div className="trigger-title">Run on a schedule</div>
                <p className="trigger-copy">
                  A workflow can run on a defined schedule for recurring
                  operational or administrative processes.
                </p>
              </div>
              <div className="trigger-card">
                <div className="trigger-title">Run when someone triggers it from the UI</div>
                <p className="trigger-copy">
                  An agent can be triggered from the UI when a team member needs
                  the workflow to run on demand.
                </p>
              </div>
            </div>

            <ul className="mini-list">
              <li>Write your reasoning in plain English.</li>
              <li>The AI produces structured outputs from that reasoning.</li>
              <li>Available building blocks are limited during beta.</li>
              <li>More building blocks will be added over time.</li>
            </ul>

            <div className="process-band">
              <div className="process-title">Approval process before marketplace listing</div>
              <ol className="process-list">
                <li>
                  Benchmark the agent using the Clinician Creator Suite's evaluation
                  tools.
                </li>
                <li>
                  An AI evaluator reviews the agent for safety and efficacy.
                </li>
                <li>
                  Asha's team reviews and approves the agent before it can be
                  distributed.
                </li>
                <li>
                  You can list the agent for free or as a paid agent. For paid
                  agents, you keep 70% of the additional revenue and Asha keeps
                  30% for distribution.
                </li>
              </ol>
            </div>
          </div>
        </section>

        <section className="s soft-section">
          <div className="s-head">
            <div className="s-num">§ 06</div>
            <div>
              <h2 className="s-title">Community expectations</h2>
              <p className="s-sub">
                Fellows receive real access and preferred pricing, so the
                program depends on real participation.
              </p>
            </div>
          </div>

          <div className="s-body">
            <div className="expectation-copy">
              <h3>Fellows are expected to contribute after the ramp</h3>
              <p>
                Once the program begins after the twelve-week mark, or whenever
                your setup is stable and working effectively, we do expect
                community contribution. There are many ways to contribute, and
                we do not want to be heavily prescriptive. However, if there is
                zero to very little involvement after several months, or if a
                Fellow violates program rules or policies, Asha reserves the
                right to remove that Fellow from the program.
              </p>
            </div>
          </div>
        </section>

        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 07</div>
            <div>
              <h2 className="s-title">Frequently asked questions</h2>
              <p className="s-sub">
                A few boundaries are important so Fellows understand what the
                program is, and what it is not.
              </p>
            </div>
          </div>

          <div className="s-body">
            <div className="faq-list">
              <div className="faq-item">
                <h3 className="faq-q">
                  Does this mean the team will build any feature for me for free?
                </h3>
                <p className="faq-a">
                  No. We give you the tools to launch AI agents using the
                  available building blocks in the Clinician Creator Suite. Separately, we
                  do try to address customer feature requests when they are
                  within our scope and useful to other offices. For example,
                  added customizability in reporting and analytics is the kind
                  of request we can often complete quickly, sometimes in two to
                  three days, whereas other vendors may take months or even a
                  year. Requests outside our scope, such as building AI
                  credential management, may be declined. Fellows do get extra
                  influence over the product roadmap, but participation does
                  not guarantee that any specific feature you request will be
                  built.
                </p>
              </div>

              <div className="faq-item">
                <h3 className="faq-q">
                  Does being a Fellow mean I can mass advertise on Asha's behalf?
                </h3>
                <p className="faq-a">
                  No. Before receiving referral incentives, you must complete
                  your first twelve weeks on the platform and we must confirm
                  that you are knowledgeable enough to share it accurately.
                  Referral activity is limited to one-on-one communication, not
                  mass advertising.
                </p>
              </div>

              <div className="faq-item">
                <h3 className="faq-q">
                  Can I partner with Asha using technology I have built?
                </h3>
                <p className="faq-a">
                  Being a Fellow does not guarantee that external technology can
                  plug into Asha. The Clinician Creator Suite gives you approved building
                  blocks for building some AI agents. External tools require
                  separate partnerships approval and approval for API access.
                </p>
              </div>

              <div className="faq-item">
                <h3 className="faq-q">What happens next?</h3>
                <p className="faq-a">
                  We're incredibly excited to transform your practice with you,
                  and we will be in touch with next steps as you make it through
                  your first few weeks of onboarding.
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="tail">
          <span>Asha Health, Inc.</span>
        </div>
      </div>
    </div>
  );
};

export default AIFellowsProgramPage;
